/**
 * The services an IEP goal can be delivered under.
 *
 * The codes are clinical.ref_service.code values, not invented ones — a goal
 * stores its service as that code (iep_goal.service_code), so anything that
 * later joins a goal to a service row (scheduling, reporting) matches without a
 * translation table.
 *
 * Kept as a small static list rather than a fetch: these three are the services
 * that actually run sessions with goals attached, and a dropdown that has to
 * wait on a round-trip before it can render is worse than one that cannot show
 * a service added last night.
 */
export const GOAL_SERVICES = [
  { code: 'aba', name: 'ABA' },
  { code: 'speech_therapy', name: 'Speech Therapy' },
  { code: 'occupational_therapy', name: 'Occupational Therapy' }
];

/**
 * Services whose goals are worked through a list of session activities — the
 * same activities the OT and SLP data sheets record against. ABA goals are
 * worked through targets instead, so they get no activity list.
 */
export const ACTIVITY_SERVICE_CODES = ['speech_therapy', 'occupational_therapy'];

/** True when goals under `code` carry an activity list. */
export const serviceHasActivities = (code) => ACTIVITY_SERVICE_CODES.includes(String(code || ''));

/** Display name for a stored service code; the code itself if it is unknown. */
export const serviceLabel = (code) => GOAL_SERVICES.find((s) => s.code === code)?.name || code || '';

/**
 * Suggested skill domains for a CUSTOM goal, by service. These seed the Skill
 * domain field's dropdown in GoalDetailsModal — the field stays free-text, so a
 * clinician can type any domain not on the list. ABA keeps the Skills Repository
 * shorthand (CRE, Mand, …); Speech and OT list full domain names, not codes.
 *
 * This is a display convenience for authoring custom goals only. It is NOT the
 * Skills Repository (that has its own domain source) — nothing here should feed
 * the repo-driven GoalBuilderModal.
 */
export const GOAL_DOMAIN_SUGGESTIONS = {
  aba: ['CRE', 'FM', 'IV', 'LR', 'LRFFC', 'Mand', 'MI', 'Tact', 'VPMTS', 'ADL', 'Echoic'],
  speech_therapy: [
    'Receptive Language',
    'Expressive Language',
    'Pre-linguistic Skills',
    'Articulation',
    'Fluency',
    'Voice',
    'Pragmatics',
    'Feeding/Swallowing'
  ],
  occupational_therapy: [
    'Fine Motor',
    'Gross Motor',
    'Visual Motor',
    'Visual Perception',
    'Sensory Processing',
    'Self-Regulation',
    'Motor Planning',
    'Bilateral Coordination',
    'Activities of Daily Living',
    'Handwriting',
    'Play and Leisure',
    'Proprioceptive - SI',
    'Tactile',
    'Cognition',
    'Social-communication',
    'Sitting Endurance',
    'Attention & focus',
    'Sensory Integration',
    'Core Strength'
  ]
};

/** The suggested skill domains for a service code, or [] for an unknown/empty one. */
export const goalDomainSuggestions = (code) => GOAL_DOMAIN_SUGGESTIONS[String(code || '')] || [];
